import type { WorkOrderDetails } from '#/graphql/types';
import { categories, impacts, priorities, states, types } from '../mappings/mappings';
import { gql } from '@apollo/client';
import { UserRole } from '#/common/types/userTypes';
import {
  getAsset,
  getAssetList,
  getFinding,
  getFindingList,
  getUser,
  getUserList,
} from '#/graphql/queries';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';
import { CustomRule } from 'devextreme-react/form';
import { color } from '@nuvolo/nuux-theme/utils';
import { LINK_TRANE_SUPPLY } from '#/config/consts';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';

export type DateComparisonValidationRule = {
  type: 'custom';
  reevaluate: true;
  validationCallback: (ruleOptions: any) => boolean;
};

const createDateComparisonValidationCallback = (
  comparisonType: 'start' | 'end',
  relatedFieldName: string,
) => {
  const isStartField = comparisonType === 'start';
  return (ruleOptions: any) => {
    const currentValue = ruleOptions.value;
    const relatedValue =
      ruleOptions.data?.[relatedFieldName] ??
      ruleOptions.formItem?.editorOptions?.form?.option('formData')?.[relatedFieldName];

    if (!currentValue || !relatedValue) return true;

    const currentDate = currentValue instanceof Date ? currentValue : new Date(currentValue);
    const relatedDate = relatedValue instanceof Date ? relatedValue : new Date(relatedValue);

    return isStartField ? currentDate <= relatedDate : currentDate >= relatedDate;
  };
};

export const createDateComparisonValidationRule = (
  comparisonType: 'start' | 'end',
  relatedFieldName: string,
): DateComparisonValidationRule => {
  return {
    type: 'custom',
    reevaluate: true,
    validationCallback: createDateComparisonValidationCallback(comparisonType, relatedFieldName),
  };
};

// Helper function to create date comparison validation rules
export const createDateComparisonRule = (
  key: string,
  comparisonType: 'start' | 'end',
  relatedFieldName: string,
) => {
  return (
    <CustomRule
      key={`${key}-validation`}
      validationCallback={createDateComparisonValidationCallback(comparisonType, relatedFieldName)}
    />
  );
};

const fields = {
  ...getBaseColumns(),
  displayNumber: {
    dataField: 'displayNumber',
    label: 'Number',
    editorOptions: { readOnly: true, placeholder: 'Auto generated' },
  },
  dueDate: {
    dataField: 'dueDate',
    label: 'Due Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      showClearButton: true,
    },
  },
  startDate: {
    dataField: 'startDate',
    label: 'Start Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'datetime',
      showClearButton: true,
    },
    children: [createDateComparisonRule('startDate', 'start', 'endDate')],
  },
  endDate: {
    dataField: 'endDate',
    label: 'End Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'datetime',
      showClearButton: true,
    },
    children: [createDateComparisonRule('endDate', 'end', 'startDate')],
  },
  duration: {
    dataField: 'duration',
    label: 'Duration (hours)',
    editorType: 'TextBox',
    editorOptions: { readOnly: true, disabled: true, placeholder: 'Auto calculated' },
  },
  plannedStartDate: {
    dataField: 'plannedStartDate',
    label: 'Planned Start Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'datetime',
      showClearButton: true,
    },
    children: [createDateComparisonRule('plannedStartDate', 'start', 'plannedEndDate')],
  },
  plannedEndDate: {
    dataField: 'plannedEndDate',
    label: 'Planned End Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'datetime',
      showClearButton: true,
    },
    children: [createDateComparisonRule('plannedEndDate', 'end', 'plannedStartDate')],
  },
  asset: {
    dataField: 'assetId',
    label: 'Asset',
    isRequired: true,
    editorType: 'RelatedItemsCards',
    editorOptions: {
      placeholder: 'Select Asset',
      displayExpr: 'assetTag',
      valueExpr: 'id',
      dropdownColumns: 1,
      button: {
        tooltip: 'View asset details',
        url: '/assets/:value/details',
        desktopOnly: false,
      },
      dataStoreOptions: {
        getItemList: gql(getAssetList),
        getItem: gql(getAsset),
        filter: ['state', 'notcontains', 'retired'],
      },
      cardConfig: {
        iconName: 'cube',
        iconColor: color('custom', 'constantTeal'),
        iconHolderOpacity: 0.2,
        subtitleExpr: 'model.name',
      },
    },
    // children: [<RequiredRule key="role-required" message="Asset is required" />],
  },
  assignedTo: {
    dataField: 'assignedToId',
    label: 'Assigned To',
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Select User',
      displayExpr: 'name',
      valueExpr: 'id',
      button: {
        tooltip: 'View user details',
        url: '/admin/users/:value/details',
        desktopOnly: false,
        enabledForRoles: [UserRole.trane_admin],
      },
      skipTenantFilter: true,
      dataStoreOptions: {
        getItemList: gql(getUserList),
        getItem: gql(getUser),
      },
      subtitleExpr: 'email',
    },
  },
  category: {
    dataField: 'category',
    label: 'Work Order Category',
    isRequired: true,
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Select Category',
      displayExpr: 'label',
      valueExpr: 'key',
      items: Object.values(categories),
    },
    // children: [<RequiredRule key="role-required" message="Category is required" />],
  },
  closedNotes: {
    dataField: 'closedNotes',
    label: 'Closed Notes',
    editorType: 'TextArea',
  },
  closedAt: {
    dataField: 'closedAt',
    label: 'Closed At',
    editorType: 'DateBox',
    editorOptions: {
      type: 'datetime',
      showClearButton: true,
    },
  },
  closedBy: {
    dataField: 'closedById',
    label: 'Closed By',
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search users...',
      button: {
        tooltip: 'View user details',
        url: '/admin/users/:value/details',
        enabledForRoles: [UserRole.trane_admin],
      },
      displayExpr: 'name',
      valueExpr: 'id',
      searchEnabled: true,
      skipTenantFilter: true,
      dataStoreOptions: {
        getItemList: gql(getUserList),
        getItem: gql(getUser),
      },
      subtitleExpr: 'email',
    },
  },
  title: {
    dataField: 'title',
    label: 'Title',
    isRequired: true,
    editorType: 'TextBox',
    // children: [<RequiredRule key="role-required" message="Title is required" />],
  },
  description: {
    dataField: 'description',
    label: 'Description',
    isRequired: true,
    editorType: 'HtmlEditor',
    editorOptions: {
      placeholder: 'Enter Description',
      height: 100,
    },
    // children: [<RequiredRule key="role-required" message="Description is required" />],
  },
  actualEffort: {
    dataField: 'actualEffort',
    label: 'Actual Effort (hours)',
    editorType: 'NumberBox',
    editorOptions: {
      min: 0,
      max: 9999,
      validationRules: [
        {
          type: 'range',
          min: 0,
          max: 9999,
          message: 'Actual Effort must be between 0 and 9999 hours.',
        },
      ],
    },
  },
  actualCostValue: {
    dataField: 'actualCostValue',
    label: 'Actual Cost',
    editorType: 'Currency',
    editorOptions: {
      readOnly: true,
      format: {
        type: 'currency',
        precision: 2,
      },
      showSpinButtons: true,
    },
  },
  impact: {
    dataField: 'impact',
    label: 'Impact',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Select Impact',
      items: Object.values(impacts),
      displayExpr: 'label',
      valueExpr: 'value',
      showClearButton: true,
    },
  },
  priority: {
    dataField: 'priority',
    label: 'Priority',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Select Priority',
      items: Object.values(priorities),
      displayExpr: 'label',
      valueExpr: 'value',
      showClearButton: true,
    },
  },
  state: {
    dataField: 'state',
    label: 'Status',
    isRequired: true,
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Select State',
      items: Object.values(states),
      displayExpr: 'label',
      valueExpr: 'key',
    },
    // children: [<RequiredRule key="role-required" message="State is required" />],
  },
  type: {
    dataField: 'type',
    label: 'Work Order Type',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Select Type',
      items: Object.values(types),
      displayExpr: 'label',
      valueExpr: 'key',
      showClearButton: true,
    },
  },
  findingId: {
    dataField: 'findingId',
    label: 'Originating Finding',
    editorType: 'RelatedItems',
    editorOptions: {
      readOnly: true,
      placeholder: 'Search findings...',
      button: {
        tooltip: 'View finding details',
        url: '/findings/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'shortDescription',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getFindingList),
        getItem: gql(getFinding),
      },
    },
  },
  traneSupplySearch: {
    editorType: 'SplitButton',
    dataField: 'traneSupplySearch',
    label: false, // label is provided on the button itself
    editorOptions: {
      mainBtnTitle: 'Search Parts',
      buttons: [
        {
          title: 'By Model',
          baseUrl: `${LINK_TRANE_SUPPLY}:value`,
          addFieldValueToUrl: 'assetModel.name',
          disabled: (record: WorkOrderDetails) =>
            !record.assetModel?.name || record.assetModel.name.trim() === unknownMappingValue.label,
        },
        {
          title: 'By Serial Number',
          baseUrl: `${LINK_TRANE_SUPPLY}:value`,
          addFieldValueToUrl: 'asset.serialNumber',
          disabled: (record: WorkOrderDetails) =>
            !record.asset?.serialNumber ||
            record.asset.serialNumber.trim() === unknownMappingValue.label,
        },
      ],
    },
  },
} satisfies FieldDefs<WorkOrderDetails & { traneSupplySearch: unknown }>;

export default fields;
